import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useWindow, type QueryResultLike } from '@krp/client-runtime'
import {
  CATALOG_WRITE,
  PhotoAttachScreen,
  buildPhotoAttachVm,
  grantsOf,
  newUuid,
  peekScanFrame,
  platformCommonProduct,
  platformSearchFeedback,
  scanEventRecorded,
  scanEventText,
  scanFrameFile,
  scanFrameUrl,
  type PhotoAttachActions,
} from '@krp/platform-ui'

import { useAppDispatch } from '../../app/hooks'
import { usePhotoUploadTask, type UploadOutcome } from './usePhotoUploadTask'
import { useMyAccess } from './useMyAccess'

interface RtkQuery<T> {
  data?: T | undefined
  error?: unknown
  isUninitialized: boolean
  isFetching: boolean
  isError: boolean
  isSuccess: boolean
}

/** Результат запроса RTK Query в виде, который читают модели экранов. */
function queryLike<T>(query: RtkQuery<T>): QueryResultLike<T> {
  return {
    ...(query.data !== undefined ? { data: query.data } : {}),
    ...(query.error !== undefined ? { error: query.error as NonNullable<QueryResultLike<T>['error']> } : {}),
    isUninitialized: query.isUninitialized,
    isLoading: query.isFetching,
    isError: query.isError,
    isSuccess: query.isSuccess,
  }
}

/**
 * Binding окна «Прикрепить фото» (RP-FB56, PHI-13a): «Для товара «…» установить это фото?». Кадр берётся с устройства (`frame` в адресе),
 * товар — из адреса окна. «Сохранить» кладёт кадр ВРЕМЕННОЙ загрузкой товара (поток PSR-5: подготовка, проверка обрезки, сохранение; ход по байтам,
 * ошибка дословно, «Повторить» с тем же ключом операции). Фото остаётся временной загрузкой, в источник оно уходит кнопкой в окне фото товара; главным
 * или дополнительным его здесь не делают. Успех: смена получает «Прикреплено» («Фото прикреплено к «…»»), окно закрывается, и человек снова на сканере
 * или экране поиска. Сбой в счётчик успехом не идёт.
 */
export function PhotoAttachBinding() {
  const handle = useWindow()
  const dispatch = useAppDispatch()
  const [params] = useSearchParams()
  const access = useMyAccess()
  const task = usePhotoUploadTask()
  const [sendFeedback] = platformSearchFeedback.useMutation()
  const productId = handle.params.productId ?? ''
  const frameId = params.get('frame') ?? ''
  // «Да, это он» из сканера: снимок сохраняется сам временной загрузкой, без вопросов.
  const confirm = params.get('confirm') === '1'
  // Номер поиска, из ответа которого человек пошёл искать товар (RP-FB77): прикреплённое фото — метка «это он» для обучения поиска.
  const searchId = params.get('searchId') ?? ''
  const autoSaved = useRef(false)
  const skip = productId === ''
  const productQuery = platformCommonProduct.useQuery({ productId }, { skip })
  const canWrite = grantsOf(access).permissions.has(CATALOG_WRITE)
  const failureLogged = useRef(false)
  const latest = useRef(handle)
  useEffect(() => {
    latest.current = handle
  }, [handle])

  const vm = buildPhotoAttachVm({
    product: queryLike(productQuery),
    newUrl: scanFrameUrl(frameId),
    frameLost: peekScanFrame(frameId) === undefined,
    upload: task.view,
    canWrite,
  })
  const productName = productQuery.data?.name ?? null

  const record = (kind: 'attached' | 'failed', text?: string) =>
    dispatch(scanEventRecorded({ kind, text: text ?? scanEventText(kind, productName), productId, at: Date.now() }))

  /** Что делать, когда загрузка кончилась: успех — записать в смену и закрыть окно. */
  const finish = (outcome: UploadOutcome) => {
    if (!outcome.ok) {
      // Отказ человека («Отмена» на проверке обрезки или отмена загрузки) — не сбой: в ленту ничего не пишем.
      if (outcome.cancelled) return
      // Сбой — не успех: «Не сохранено — повторить» в ленту один раз, ошибку и «Повторить» показывает сама загрузка.
      if (!failureLogged.current) {
        failureLogged.current = true
        record('failed')
      }
      return
    }
    record('attached', confirm ? `Запомнили: этот снимок теперь помогает находить «${productName ?? 'товар'}»` : undefined)
    // «Да, это он» отметил выбор сам при нажатии; здесь — только товар, найденный вручную через «Найти товар и прикрепить фото».
    if (!confirm && searchId !== '') {
      void sendFeedback({ operationId: newUuid(), searchId, action: 'attach', productId })
        .unwrap()
        .catch((error: unknown) => {
          const text = (error as { message?: string } | null)?.message ?? 'ответ сервера не получен'
          record('failed', `Отметка для обучения поиска не сохранилась: ${text}`)
        })
    }
    latest.current.close()
  }

  const actions: PhotoAttachActions = {
    save: () => {
      const stored = peekScanFrame(frameId)
      if (stored === undefined) return
      failureLogged.current = false
      void task.run(productId, scanFrameFile(stored), scanFrameUrl(frameId)).then(finish)
    },
    cancel: () => handle.close(),
    cancelUpload: task.cancel,
    retryUpload: () => void task.retry().then(finish),
    dismissUpload: task.dismiss,
    acceptCrop: (box) => void task.accept(box),
    reload: () => {
      void productQuery.refetch()
    },
  }
  // Подтверждение «это он»: как только товар прочитан и ничто не мешает, снимок отправляется сам. Мешает что-то (снимок
  // потерян, нет права) — окно остаётся с объяснением, как обычное прикрепление.
  const saveNow = useRef(actions.save)
  useEffect(() => {
    saveNow.current = actions.save
  })
  const blocked = vm.blocker !== null
  useEffect(() => {
    if (!confirm || blocked || autoSaved.current) return
    autoSaved.current = true
    saveNow.current()
  }, [blocked, confirm])
  return <PhotoAttachScreen vm={vm} actions={actions} />
}
